import { useStores } from "stores/setup/use-store";
import { useMemo, useCallback } from "react";
import { formatTimelineRadarMarker } from "util/formatTimelineMarker/formatTimelineRadarMarker";
import { ProductAnalyticsEvents } from "enum/ProductAnalyticsEvents";

export function useRadarTimelineData() {
  const {
    dataStores: { radarMapStore, userConfigStore },
  } = useStores();

  // Rainbow AI: 26 frames (10 past + 1 current + 15 forecast)
  const { pastLength, nowcastLength, totalFrames, allFrames } = useMemo(() => {
    const pastFrames = radarMapStore.frames.filter((f) => f.type === "past");
    const currentFrame = radarMapStore.frames.filter(
      (f) => f.type === "current"
    );
    const futureFrames = radarMapStore.frames.filter(
      (f) => f.type === "future"
    );

    // Convert Rainbow frames to timeline format (compatible with existing TimelineRadarSlider)
    const convertedFrames = radarMapStore.frames.map((frame) => ({
      time: frame.timestamp * 1000, // Convert to milliseconds
      path: frame.tileUrlPattern,
    }));

    return {
      pastLength: pastFrames.length + currentFrame.length, // Past + current (11 frames)
      nowcastLength: futureFrames.length, // Forecast frames (15 frames) - labeled as "nowcast" for UI
      totalFrames: radarMapStore.frames.length, // 26 total
      allFrames: convertedFrames,
    };
  }, [radarMapStore.frames]);

  const onTogglePlayPause = useCallback(() => {
    radarMapStore.isPlaying ? radarMapStore.stop() : radarMapStore.play();
  }, [radarMapStore.isPlaying, radarMapStore.play, radarMapStore.stop]);

  const timeMarkers = useMemo(() => {
    return Array.from({ length: 5 }, (_, i) => {
      const index = Math.round((i / 4) * (totalFrames - 1));
      return {
        index,
        label: formatTimelineRadarMarker(
          allFrames[index]?.time,
          userConfigStore.timezone
        ),
      };
    });
  }, [allFrames, totalFrames, userConfigStore.timezone]);

  const productAnalyticsEvent = () => {
    radarMapStore.addProductAnalyticsEvent(
      ProductAnalyticsEvents.ForecastRadarLayerMapStart,
      { environmental_variable: "precip" }
    );
  };

  return {
    currentIndex: radarMapStore.currentIndex,
    isPlaying: radarMapStore.isPlaying,
    isNowcast: radarMapStore.isNowcast,
    elapsedTime: radarMapStore.elapsedTime,
    totalFrames,
    pastLength,
    nowcastLength,
    allFrames,
    onTogglePlayPause,
    timeMarkers,
    onChangeIndex: (index: number) => radarMapStore.setCurrentIndex(index),
    productAnalyticsEvent: () => productAnalyticsEvent(),
  };
}
